import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import { animeList, categories, siteConfig } from '@/mocks/anime';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const anime = animeList.find((a) => a.id === Number(id));

  if (!anime) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-600 mb-4 block"></i>
            <h1 className="text-xl font-heading font-bold text-foreground-200 mb-2">作品未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该内容可能已下架或不存在</p>
            <Link to="/" className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const currentCategory = categories.find((c) => c.slug === anime.category);
  const relatedAnime = animeList
    .filter((a) => a.id !== anime.id && (a.category === anime.category || a.tags.some((t) => anime.tags.includes(t))))
    .slice(0, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: anime.title,
    description: anime.description,
    datePublished: anime.publishDate,
    author: {
      '@type': 'Organization',
      name: siteConfig.name,
    },
    publisher: {
      '@type': 'Organization',
      name: siteConfig.name,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: siteConfig.url,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: currentCategory?.name || '分类',
        item: `${siteConfig.url}/category/${anime.category}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: anime.title,
        item: `${siteConfig.url}/article/${anime.id}`,
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-accent-400 transition-colors whitespace-nowrap cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to={`/category/${anime.category}`} className="hover:text-accent-400 transition-colors whitespace-nowrap cursor-pointer">
              {currentCategory?.name || '分类'}
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-200">{anime.title}</span>
          </nav>

          <article className="flex flex-col lg:flex-row gap-8 md:gap-10">
            <div className="flex-shrink-0 w-full lg:w-[300px]">
              <div className="rounded-lg overflow-hidden bg-background-100">
                <img
                  src={anime.coverImage}
                  alt={anime.title}
                  title={`${anime.title} - 樱花动漫网免费高清在线观看`}
                  className="w-full aspect-[3/4] object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 mb-4">
                {anime.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="flex items-center gap-1">
                  <i className="ri-star-fill text-secondary-400"></i>
                  <span className="text-sm font-semibold text-secondary-400">{anime.rating}</span>
                </div>
                <span className="text-foreground-600">|</span>
                <span className="text-sm text-foreground-400">{anime.updateInfo}</span>
                <span className="text-foreground-600">|</span>
                <span className="text-sm text-foreground-400">{anime.publishDate}</span>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {anime.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 text-xs rounded-full bg-accent-100/10 text-accent-400 border border-accent-200/20">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="prose prose-invert max-w-none mb-8">
                <p className="text-sm md:text-base text-foreground-300 leading-relaxed">
                  {anime.description}
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-lg font-heading font-semibold text-foreground-100 mb-4">
                  <i className="ri-play-list-2-line text-accent-400 mr-2"></i>剧集列表
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {anime.episodes.map((ep) => (
                    <a
                      key={ep.number}
                      href={ep.url}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-background-100 hover:bg-primary-500/15 border border-background-200/50 hover:border-primary-500/30 transition-all cursor-pointer group whitespace-nowrap"
                    >
                      <i className="ri-play-circle-line text-primary-400 group-hover:text-primary-300 text-sm"></i>
                      <span className="text-sm text-foreground-200 group-hover:text-foreground-50 truncate">
                        {ep.title}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </article>

          {relatedAnime.length > 0 && (
            <section className="mt-12 md:mt-16">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50 mb-6">
                <i className="ri-links-line text-accent-400 mr-2"></i>相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedAnime.map((item) => (
                  <Link
                    key={item.id}
                    to={`/article/${item.id}`}
                    className="group block rounded-lg overflow-hidden bg-background-100 hover:bg-background-200/70 transition-all cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] overflow-hidden">
                      <img
                        src={item.coverImage}
                        alt={item.title}
                        title={`${item.title} - 樱花动漫网相关推荐`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                      />
                      <div className="absolute top-2 right-2 px-2 py-1 bg-background-950/80 backdrop-blur-sm rounded-md">
                        <span className="text-xs font-medium text-secondary-400">{item.rating}</span>
                      </div>
                    </div>
                    <div className="p-3 md:p-4">
                      <h3 className="text-sm font-medium text-foreground-100 group-hover:text-accent-400 transition-colors line-clamp-1">
                        {item.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{item.updateInfo}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}